import { memo, useId, useMemo } from "react";
import { useLocation } from "react-router";

import { cn } from "@/lib/chillnn/cn";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion/accordion";

type Props = {
	children: React.ReactNode;
	title: string;
	childrenPaths: string[];
	icon: React.ReactNode;

	/**
	 * isActiveの判定に曖昧判定を用いるかどうか
	 */
	useAmbiguousMatching?: boolean;
	/**
	 * 除外するパス
	 */
	excludePaths?: string[];
};

export const SideMenuItemWrapper = memo(
	(
		{ title, children, childrenPaths, icon, useAmbiguousMatching = false, excludePaths }: Props, //
	) => {
		const uniqueId = useId();

		const { pathname } = useLocation();

		const isActive = useMemo(
			() =>
				(function () {
					// pathnameの最後に '/' が存在していた場合排除する
					const trimmedPathname = pathname.at(-1) === "/" ? pathname.slice(0, -1) : pathname;

					if (excludePaths?.includes(trimmedPathname)) {
						// 除外するパスが設定されている & 除外するパスと現在のパスが完全一致する場合
						return false;
					}

					// いずれかでも一致していればtrueを返す
					return childrenPaths.some((path) => {
						if (useAmbiguousMatching) {
							// 曖昧一致を行う場合
							return trimmedPathname.startsWith(path);
						} else {
							return trimmedPathname === path;
						}
					});
				})(),
			[childrenPaths, excludePaths, pathname, useAmbiguousMatching],
		);

		const defaultValue = useMemo(() => {
			if (isActive) {
				return uniqueId;
			}

			return undefined;
		}, [isActive, uniqueId]);

		return (
			<Accordion type={"single"} collapsible className="py-0" defaultValue={defaultValue}>
				<AccordionItem value={uniqueId} className="border-0">
					<AccordionTrigger className="w-full rounded px-0.5 py-0.5 pr-2 hover:bg-zinc-100 hover:no-underline">
						<div className="group flex items-center justify-between p-2">
							<div className="flex items-center gap-3">
								<div className={cn(isActive && "text-brand")}>{icon}</div>
								<h5
									className={cn(
										"text-[15px] leading-[24px] text-zinc-500",
										isActive && "font-semibold text-zinc-900",
									)}
								>
									{title}
								</h5>
							</div>
						</div>
					</AccordionTrigger>
					<AccordionContent className="text-base">{children}</AccordionContent>
				</AccordionItem>
			</Accordion>
		);
	},
);
